import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Col, Container } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
import Overlay from '../shared/components/Overlay';
import {
  useDeleteUserCookieMutation,
  useGetAccountDataQuery,
  useGetConfigurationQuery,
} from '../shared/navigation/apiSlice';
import NestedConvert from '../shared/components/Navigation/NestedConvert';
import {
  adminLayoutSelector,
  setPayByUserAgreements,
} from './adminLayoutSlice';
import { accountSelector } from '../shared/store/shared/accountSlice';
import { userSelector } from '../shared/store/shared/userSlice';
import styles from './admin.module.scss';
import AccountSwitcher from './AccountSwitcher';
import { AscendingSortDirection } from '../shared/components/ResponsiveTable/models';
import { useGetAccountListQuery } from './AccountSelect/apiSlice';
import ErrorPage from '../shared/components/ErrorPage';
import useAddDealUsersCompletedSignalR from './useAddDealUsersCompletedSignalR';
import AlertGroup from '../shared/components/AlertGroup';
import TutorialCoachMarks from '../shared/components/TutorialCoachMarks';
import { reportCoachMarks, reportNumberOfDeals } from '../shared/analytics';
import { useGetUserPreferencesQuery } from '../shared/components/AdminWelcomeModal/apiSlice';
import { PLATFORM_TOUR_SESSION_STORAGE_KEY } from '../shared/constants';
import {
  hideTutorial,
  platformTourSelector,
  showPopupReminder,
} from '../shared/store/shared/platformTourSlice';
import Footer from '../shared/components/Footer';
import { useGetDealsListQuery } from '../shared/store/shared/dealsApiSlice';
import useAgreementAnalytics from '../shared/hooks/useAgreementAnalytics';
import { dealHasExpired } from '../shared/utils/dataHelpers';
import { setActiveDeals } from '../shared/store/shared/activeDealsSlice';
import SideNav from '../shared/components/Navigation/SideNav';
import MobileNav from '../shared/components/Navigation/MobileNav';
import { NavItem } from '../shared/navigation/navigationRouting';
import useSignalREventConnector from '../shared/hooks/useSignalRConnector';
import { NewDealNotification } from '../shared/models/DealModel';

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isConfirmationPage = location.pathname.includes('/confirmation');
  const { pageTitle, sidebar, messages } = useSelector(adminLayoutSelector);
  const { currentAccountId } = useSelector(accountSelector);
  const { showPlatformTour } = useSelector(platformTourSelector);
  const [renderTutorial, setRenderTutorial] = useState(false);
  const [dealUserCookies] = useDeleteUserCookieMutation();
  const {
    data: rawAdminConfig,
    isFetching,
    isLoading,
  } = useGetConfigurationQuery(
    {
      type: 'admin',
      accountId: currentAccountId,
    },
    {
      skip: !currentAccountId,
    },
  );

  const adminConfig = useMemo(() => {
    const menus = [];
    if (!isLoading && !isFetching && rawAdminConfig) {
      const menu: NavItem | null = rawAdminConfig?.menus
        ? { ...rawAdminConfig.menus[0] }
        : null;
      if (menu) {
        menu.children = menu.children.map((child) => {
          const item = {
            ...child,
            ...{
              link: {
                href: child.href,
              },
            },
          };
          item.href = item?.link?.href
            ?.toString()
            .replace('{accountId}', currentAccountId);
          item.link.href = item.href;
          return item;
        });
      }
      menus.push(menu);
    }
    return { ...rawAdminConfig, menus };
  }, [rawAdminConfig, isLoading, isFetching, currentAccountId]);
  // Called to be cached for performance enhancement
  useGetUserPreferencesQuery();

  const { value: user } = useSelector(userSelector);
  const hasCompleted2Fa = Boolean(
    user?.access_token && jwtDecode<any>(user.access_token)?.rbac_2fa_raids,
  );

  // Called to be cached for performance enhancement
  useGetDealsListQuery(currentAccountId, {
    skip: !currentAccountId || !hasCompleted2Fa,
  });

  const {
    data: accountData,
    isLoading: isAccountDataLoading,
    isFetching: isAccountDataFetching,
    error: accountDataError,
  } = useGetAccountDataQuery(currentAccountId, {
    skip: !currentAccountId,
  });

  const {
    data: accountList,
    isLoading: isAccountListLoading,
    isFetching: isAccountListFetching,
  } = useGetAccountListQuery(
    {
      page: 1,
      pageSize: 3,
      sort: AscendingSortDirection,
    },
    { skip: !currentAccountId },
  );

  const { data: deals, isLoading: dealsLoading } = useGetDealsListQuery(
    currentAccountId,
    { skip: !currentAccountId || !hasCompleted2Fa },
  );

  const isAccountLoading =
    isAccountDataLoading ||
    isAccountDataFetching ||
    isAccountListLoading ||
    isAccountListFetching;

  const newAdminRoute = useMemo(() => {
    if (
      isFetching ||
      isLoading ||
      isAccountDataLoading ||
      isAccountDataFetching ||
      !adminConfig ||
      !accountData
    ) {
      return [];
    }
    if (accountData?.access && adminConfig.menus && adminConfig.menus[0]) {
      adminConfig.menus[0].children = adminConfig.menus[0].children.filter(
        (e: { title: string }) =>
          (e.title !== 'Store' || accountData.access.orderAccess) &&
          (e.title !== 'Order History' ||
            accountData.access.orderHistoryAccess) &&
          (e.title !== 'ATP Hub' ||
            accountData.engagements.includes('ATP ILT')) &&
          (e.title !== 'Courses' ||
            accountData.engagements.includes('ATP ILT')),
      );
    }

    return NestedConvert([...adminConfig.menus]);
  }, [isFetching, isLoading, isAccountDataLoading, isAccountDataFetching]);

  useEffect(() => {
    if (pageTitle) {
      if (accountData && accountData?.name) {
        document.title = `${pageTitle} - ${accountData?.name} | PMI Partner Platform`;
        if (accountData?.hasPayByUserDeal) {
          dispatch(setPayByUserAgreements(accountData.payByUserDeals ?? []));
        }
      } else {
        document.title = `${pageTitle} | PMI Partner Platform`;
      }
    } else {
      document.title = 'PMI Partner Platform';
    }
  }, [accountData, pageTitle]);

  useEffect(() => {
    const { menus, accountPermissions } = adminConfig;
    if (
      currentAccountId &&
      menus?.length === 0 &&
      accountPermissions?.length === 0
    ) {
      navigate('/admin/');
    }
  }, [adminConfig, currentAccountId]);

  useAddDealUsersCompletedSignalR();

  useSignalREventConnector(
    'newagreementadded',
    (response: NewDealNotification) => {
      if (
        response &&
        location.pathname.includes(`admin/${response.AccountNumber}`)
      ) {
        dealUserCookies().unwrap();
      }
    },
  );

  const handlePlatformSession = (linkTitle: string, linkModule: string) => {
    dispatch(hideTutorial());
    setRenderTutorial(false);
    reportCoachMarks(linkTitle, linkModule);
    if (!sessionStorage.getItem(PLATFORM_TOUR_SESSION_STORAGE_KEY)) {
      dispatch(showPopupReminder());
    }
  };

  useEffect(() => {
    if (showPlatformTour) {
      dispatch(hideTutorial());
    }
  }, [location.pathname]);

  useEffect(() => {
    setRenderTutorial(showPlatformTour);
  }, [showPlatformTour]);

  const agreementAnalytics = useAgreementAnalytics();

  useEffect(() => {
    if (currentAccountId && !dealsLoading) {
      if (deals && deals.length > 0) {
        dispatch(setActiveDeals(deals.filter((d) => !dealHasExpired(d))));
        reportNumberOfDeals(
          currentAccountId,
          deals.filter((d) => !dealHasExpired(d)).length,
        );
      }
    }
  }, [currentAccountId, dealsLoading]);

  useEffect(() => {
    if (!location.pathname.includes('admin/select-account')) {
      agreementAnalytics.sendData();
    }
  }, [location]);

  const navigationLinks = useMemo(
    () =>
      newAdminRoute.length > 0
        ? newAdminRoute[0]?.children.map((i: NavItem) => ({
            ...i,
            link: { href: i.href, text: i.title },
          }))
        : [],
    [newAdminRoute],
  );

  if (
    accountDataError &&
    'status' in accountDataError &&
    accountDataError.status === 404
  ) {
    return (
      <ErrorPage status={404} message="Page Not Found">
        <h2>The account you are looking for cannot be found.</h2>
        <p>You may have followed a bad link or incorrectly typed the URL.</p>
      </ErrorPage>
    );
  }

  return (
    <>
      <MobileNav items={navigationLinks} title="Partner Platform (Admin)" />
      <Container
        fluid
        className={`d-flex flex-column min-vh-100 p-0 ${styles.adminBg}`}
      >
        <div className="d-flex flex-grow-1">
          {currentAccountId && sidebar ? (
            <Col
              xs={0}
              md={3}
              lg={2}
              className={`px-0 ${styles.leftContainer} z-1`}
            >
              <SideNav
                title="Partner Platform (Admin)"
                items={navigationLinks}
              />
            </Col>
          ) : null}
          <Col
            xs={12}
            md={currentAccountId ? 9 : 12}
            lg={10}
            className={`p-xxl-5 py-3 px-2 mx-auto max-xl-container ${styles.rightContainer}`}
          >
            <div className="d-flex flex-column pt-xxl-2 ps-xxl-4 pe-xxl-2 ps-1 pe-1">
              {!isConfirmationPage && (
                <>
                  <AlertGroup messages={messages} />
                  <div className="d-flex flex-wrap align-items-start pb-2 mb-3 w-100">
                    <div className="me-auto pe-2">
                      <p className={styles.greeting}>
                        {accountData?.name} Partner Platform
                      </p>
                      <h1
                        className={`${styles.pageTitle} admin-page-header-title mb-0`}
                      >
                        {pageTitle}
                      </h1>
                    </div>
                    <div className="d-sm-none w-100" />
                    {currentAccountId &&
                      accountData?.name &&
                      accountList?.accounts?.totalItemCount > 1 && (
                        <AccountSwitcher
                          accountName={accountData?.name}
                          accountList={accountList}
                          currentAccountId={currentAccountId}
                        />
                      )}
                  </div>
                </>
              )}
              <Outlet />
            </div>
          </Col>
        </div>
        <Footer />
      </Container>
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={isLoading || isFetching || isAccountLoading}
      />
      {renderTutorial && (
        <TutorialCoachMarks
          btnTracking={(linkTitle, linkModule) => {
            handlePlatformSession(linkTitle, linkModule);
          }}
        />
      )}
    </>
  );
}
